// Thin fetch wrapper. Auth0 bearer token is injected per request by the hook
// in `useApi()` below, so this module stays framework-free.

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// SPA and API live on different origins in production. In dev, Vite proxies
// /api/* → http://localhost:8787, so VITE_API_BASE is empty and `path`
// stays relative; in prod we prefix the deployed Worker URL.
export function apiUrl(path: string): string {
  const base = import.meta.env.VITE_API_BASE ?? "";
  if (!base) return path;
  if (/^https?:\/\//i.test(path)) return path;
  return `${base.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;
}

export async function apiFetch<T>(
  path: string,
  opts: { token?: string; tenantId?: number; method?: string; body?: unknown } = {},
): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (opts.token) headers.authorization = `Bearer ${opts.token}`;
  if (opts.body) headers["content-type"] = "application/json";
  // v3 — active tenant. Until the OrgSwitcher ships we send the tenant
  // resolved by /api/auth/me; for single-tenant Etus that's always 1.
  if (opts.tenantId != null) headers["x-tenant-id"] = String(opts.tenantId);

  const res = await fetch(apiUrl(path), {
    method: opts.method ?? "GET",
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });

  // If we got HTML back the request hit the SPA host instead of the API
  // (typical when VITE_API_BASE isn't set or is wrong). Fail loudly so the
  // shape mismatch doesn't propagate as `undefined.field` deep in render.
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("text/html")) {
    throw new ApiError(
      res.status,
      `Expected JSON from API but got HTML — check VITE_API_BASE (${import.meta.env.VITE_API_BASE ?? "(empty)"})`,
      undefined,
    );
  }

  const text = await res.text();
  const parsed = text ? safeJson(text) : undefined;
  if (!res.ok) throw new ApiError(res.status, `${res.status} ${res.statusText}`, parsed);
  return parsed as T;
}

function safeJson(s: string): unknown {
  try { return JSON.parse(s); } catch { return s; }
}
